/**
 * A `text/event-stream` (Server-Sent Events) reader built on `fetch`.
 *
 * The browser's `EventSource` can't send custom headers, and nest needs the HMAC signature and the Parse
 * session on every request. So we read the stream body ourselves, and re-sign on every (re)connect.
 */

export type TStreamMessage = {
    eventName: string
    data: string
}

export type TEventStreamStatus = 'connecting' | 'open' | 'reconnecting' | 'closed'

export class EventStreamHttpError extends Error {
    status: number

    constructor (status: number) {
        super(`Event stream request failed with status ${status}`)
        this.name = 'EventStreamHttpError'
        this.status = status
    }
}

/**
 * Incremental parser for the event-stream wire format. Feed it decoded text in whatever chunks the network
 * delivers; it calls `onMessage` once per complete message (a blank line ends a message).
 * Follows the WHATWG rules we rely on: `event:` / `data:` fields, multi-line data joined with '\n',
 * `:` comment lines (heartbeats), CRLF / LF / CR line endings, and messages without data are dropped.
 */
export const createEventStreamParser = ({ onMessage, onComment }: {
    onMessage: (message: TStreamMessage) => void
    onComment?: (comment: string) => void
}) => {
    let pendingText = ''
    let eventName = ''
    let dataLines: string[] = []

    const dispatchMessage = () => {
        const hasData = dataLines.length > 0
        const message = { eventName: eventName || 'message', data: dataLines.join('\n') }
        eventName = ''
        dataLines = []
        if (hasData) {
            onMessage(message)
        }
    }

    const processLine = (line: string) => {
        if (line === '') {
            dispatchMessage()
            return
        }
        if (line.startsWith(':')) {
            onComment?.(line.slice(1).trimStart())
            return
        }

        const colonIndex = line.indexOf(':')
        const field = colonIndex === -1 ? line : line.slice(0, colonIndex)
        const rawValue = colonIndex === -1 ? '' : line.slice(colonIndex + 1)
        const value = rawValue.startsWith(' ') ? rawValue.slice(1) : rawValue

        if (field === 'event') {
            eventName = value
        } else if (field === 'data') {
            dataLines.push(value)
        }
        // `id` and `retry` aren't used by our servers; unknown fields are ignored per the spec.
    }

    const push = (textChunk: string) => {
        pendingText += textChunk
        let lineStart = 0
        for (let i = 0; i < pendingText.length; i++) {
            const char = pendingText[i]
            if (char !== '\n' && char !== '\r') {
                continue
            }
            // A trailing CR may be the first half of a CRLF split across chunks; wait for the next chunk.
            if (char === '\r' && i === pendingText.length - 1) {
                break
            }
            processLine(pendingText.slice(lineStart, i))
            if (char === '\r' && pendingText[i + 1] === '\n') {
                i++
            }
            lineStart = i + 1
        }
        pendingText = pendingText.slice(lineStart)
    }

    return { push }
}

// 4xx responses other than timeouts/rate limits won't get better by retrying (bad auth, missing game).
const isRetryableStatus = (status: number) => status >= 500 || status === 408 || status === 429

/**
 * Opens a long-lived event stream that reconnects with exponential backoff until `close()` is called
 * or the server answers with a non-retryable status.
 *
 * `buildRequest` runs before every attempt so each connection gets a fresh signature.
 * `onOpen` fires on every successful connect (`isReconnect` is false only for the first one); callers
 * use reconnects to refetch anything they may have missed while disconnected.
 * If nothing arrives (not even a heartbeat comment) within `idleTimeoutMs`, the connection is treated as
 * dead and replaced.
 */
export const openEventStream = ({
    buildRequest,
    onMessage,
    onOpen,
    onStatusChange,
    onFatalError,
    fetchImpl = (input, init) => fetch(input, init),
    initialRetryDelayMs = 1000,
    maxRetryDelayMs = 15000,
    idleTimeoutMs = 45000,
}: {
    buildRequest: () => Promise<{ url: string; headers: Record<string, string> }>
    onMessage: (message: TStreamMessage) => void
    onOpen?: (params: { isReconnect: boolean }) => void
    onStatusChange?: (status: TEventStreamStatus) => void
    onFatalError?: (error: EventStreamHttpError) => void
    fetchImpl?: (input: string, init: RequestInit) => Promise<Response>
    initialRetryDelayMs?: number
    maxRetryDelayMs?: number
    idleTimeoutMs?: number
}) => {
    let isClosed = false
    let hasConnectedBefore = false
    let failedAttemptCount = 0
    let activeAbortController: AbortController | null = null
    let idleTimer: ReturnType<typeof setTimeout> | null = null
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    let resolveRetryWait: (() => void) | null = null

    const setStatus = (status: TEventStreamStatus) => {
        onStatusChange?.(status)
    }

    const clearIdleTimer = () => {
        if (idleTimer) {
            clearTimeout(idleTimer)
            idleTimer = null
        }
    }

    const resetIdleTimer = (abortController: AbortController) => {
        clearIdleTimer()
        idleTimer = setTimeout(() => abortController.abort(), idleTimeoutMs)
    }

    const waitBeforeRetry = async () => {
        const backoffDelayMs = Math.min(maxRetryDelayMs, initialRetryDelayMs * 2 ** failedAttemptCount)
        // Jitter spreads reconnects out so a server restart isn't hit by every player at the same instant.
        const retryDelayMs = backoffDelayMs / 2 + Math.random() * (backoffDelayMs / 2)
        failedAttemptCount++
        await new Promise<void>(resolve => {
            resolveRetryWait = resolve
            retryTimer = setTimeout(resolve, retryDelayMs)
        })
        resolveRetryWait = null
        retryTimer = null
    }

    const readOneConnection = async () => {
        const abortController = new AbortController()
        activeAbortController = abortController
        const request = await buildRequest()
        const response = await fetchImpl(request.url, {
            headers: request.headers,
            signal: abortController.signal,
            cache: 'no-store',
        })
        if (!response.ok || !response.body) {
            throw new EventStreamHttpError(response.status)
        }

        setStatus('open')
        onOpen?.({ isReconnect: hasConnectedBefore })
        hasConnectedBefore = true

        const parser = createEventStreamParser({
            onMessage: (message) => {
                // A delivered message proves the connection is healthy, so the next drop starts backoff over.
                failedAttemptCount = 0
                try {
                    onMessage(message)
                } catch (handlerError) {
                    // A bug in a handler shouldn't tear the connection down (the retry loop would hide it).
                    // Rethrow outside the read loop so it still reaches the console and Sentry.
                    setTimeout(() => {
                        throw handlerError
                    })
                }
            },
        })
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        resetIdleTimer(abortController)
        try {
            for (;;) {
                const { value, done } = await reader.read()
                if (done || isClosed) {
                    break
                }
                resetIdleTimer(abortController)
                parser.push(decoder.decode(value, { stream: true }))
            }
        } finally {
            clearIdleTimer()
            reader.releaseLock()
        }
    }

    const run = async () => {
        setStatus('connecting')
        while (!isClosed) {
            try {
                await readOneConnection()
            } catch (error) {
                // Network failures (TypeError), idle/close aborts (AbortError), and retryable HTTP statuses
                // end up here and are retried. HTTP errors the server won't recover from stop the stream.
                if (error instanceof EventStreamHttpError && !isRetryableStatus(error.status)) {
                    isClosed = true
                    setStatus('closed')
                    onFatalError?.(error)
                    return
                }
            }
            if (isClosed) {
                break
            }
            setStatus('reconnecting')
            if (isClosed) {
                break
            }
            await waitBeforeRetry()
        }
        setStatus('closed')
    }

    const close = () => {
        if (isClosed) {
            return
        }
        isClosed = true
        clearIdleTimer()
        activeAbortController?.abort()
        if (retryTimer) {
            clearTimeout(retryTimer)
        }
        resolveRetryWait?.()
    }

    const finished = run()

    return { close, finished }
}
